import React from 'react';
import { faTerminal } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/semantic-graphs-function-calling.jpg
import semanticGraphsImg from '../../../assets/images/blogs/Rise-of-AI-Applications.png';

const SemanticGraphsFunctionCalling = () => (
    <BlogPost
        title="Semantic Graphs and AI Function Calling"
        subtitle="Function classification and parameter extraction running locally on a laptop. No API calls to external LLMs. Sub-30ms latency."
        category="Architecture"
        categoryIcon={faTerminal}
        color="#003366"
        date="March 2026"
        readTime="5 min read"
        image={semanticGraphsImg}
        imageAlt="Semantic graphs for AI function calling - natural language to executable commands"
        ctaHeading="Want local, Consistentfunction calling?"
        ctaText="Book a discovery call and we'll show you how Nebulyx Cognition enables sub-30ms natural language command processing."
        seoDescription="How semantic graphs enable accurate slot filling and function calling with sub-30ms latency, running locally without external LLM API calls."
        seoKeywords="function calling, slot filling, semantic graphs, natural language processing, ConsistentAI, Nebulyx Cognition"
        slug="semantic-graphs-function-calling"
    >
        <p>
            I'm back online after a couple of weeks heads-down building, with my team,
            a number of customer use cases on the Nebulyx AI Platform.
        </p>

        <p>
            One area we've been deep in: <strong>natural language command processing</strong>.
            The kind where a user types "Show me overdue tasks assigned to Sarah" and the
            system figures out not just what they want, but extracts the right parameters
            to actually execute it.
        </p>

        <h3>Semantic Graphs for Slot Filling</h3>

        <p>
            What's been interesting is the approach we're taking with semantic graphs for
            slot filling.
        </p>

        <p>
            Slot filling is the process of extracting the specific values needed to complete
            a function call - in "Show overdue tasks for Sarah", that means recognising
            "tasks" as the entity type, "overdue" as a status filter, and "Sarah" as the
            owner.
        </p>

        <p>
            Rather than throwing everything at a massive cloud model, we're using our
            lightweight <strong>Nebulyx Cognition™️</strong> ConsistentAI model.
        </p>

        <p>
            It lets us navigate from the ask (in natural language) to the intent in a way
            that's both interpretable and surprisingly accurate.
        </p>

        <h3>The Result</h3>

        <blockquote className="blog-post-quote">
            "Function classification and parameter extraction running locally on a laptop.
            No API calls to external LLMs for the core classification loop. Sub-30ms latency."
        </blockquote>

        <h3>Beyond Classification</h3>

        <p>
            But classification is just the first step. Once you know the intent and
            parameters, you need to actually do something with them.
        </p>

        <p>
            That's where it gets interesting - the same command interface can route to
            internal databases, external knowledge bases like SharePoint, or business
            engines for risk calculations and financial modelling.
        </p>

        <p>
            A single query might pull static reference data, call a calculation engine,
            and combine everything into a clean, business-friendly UI. No context switching,
            no hunting through multiple systems.
        </p>

        <h3>A Robust Agent Tooling Architecture</h3>

        <p>
            This is what makes it a robust agent tooling architecture:
        </p>

        <p>
            <strong>The semantic layer</strong> handles the natural language understanding.
        </p>

        <p>
            <strong>The function registry</strong> defines what's possible.
        </p>

        <p>
            <strong>The execution layer</strong> knows how to talk to each downstream system.
        </p>

        <p>
            Consistentinference with auditable paths and consistent results.
        </p>

        <h3>Real-World Application</h3>

        <p>
            We're seeing this pattern work well for compliance workflows - finding tasks,
            creating tasks, checking deadlines, assigning work. Commands that would normally
            require either rigid keyword matching or expensive LLM inference on every
            keystroke.
        </p>

        <p>
            Local. Fast. Deterministic. 🎯
        </p>
    </BlogPost>
);

export default SemanticGraphsFunctionCalling;